import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import CollegeSearch from "../components/CollegeSearch";
import {
  User, Mail, Phone, Lock, Eye, EyeOff, AlertCircle,
  GraduationCap, Users, MapPin, BookOpen,
} from "lucide-react";

const COLLEGES = [
  // ── IITs & NITs ──
  "IIT Bombay", "IIT Delhi", "IIT Madras", "IIT Kharagpur", "IIT Roorkee",
  "VNIT Nagpur", "NIT Trichy", "NIT Surathkal",
  // ── BITS ──
  "BITS Pilani", "BITS Goa",
  // ── Mumbai Region ──
  "Veermata Jijabai Technological Institute (VJTI)",
  "Institute of Chemical Technology (ICT Mumbai)",
  "Sardar Patel Institute of Technology (SPIT)",
  "K.J. Somaiya College of Engineering",
  "Thadomal Shahani Engineering College",
  "NMIMS University",
  "D.J. Sanghvi College of Engineering",
  "Thakur College of Engineering",
  "Fr. Conceicao Rodrigues College of Engineering",
  "Pillai College of Engineering",
  "Terna Engineering College",
  "St. Francis Institute of Technology",
  "Atharva College of Engineering",
  "Vidyalankar Institute of Technology",
  "Universal College of Engineering",
  "SIES Graduate School of Technology",
  // ── Pune Region ──
  "Pune Institute of Computer Technology (PICT)",
  "College of Engineering Pune (COEP)",
  "Symbiosis Institute of Technology (SIT)",
  "MIT College of Engineering, Pune",
  "VIT Pune",
  "Vishwakarma Institute of Technology (VIT Pune)",
  "Cummins College of Engineering for Women",
  "Sinhgad College of Engineering (SCOE)",
  "NBN Sinhgad School of Engineering",
  "Pimpri Chinchwad College of Engineering (PCCOE)",
  "Indira College of Engineering and Management",
  "Army Institute of Technology, Pune",
  "DY Patil College of Engineering, Pune",
  "Bharati Vidyapeeth College of Engineering, Pune",
  "Zeal College of Engineering and Research",
  "Marathwada Mitra Mandal's College of Engineering",
  "Fergusson College, Pune",
  "Symbiosis College of Arts and Commerce",
  // ── Nagpur Region ──
  "Yeshwantrao Chavan College of Engineering (YCCE)",
  "KDK College of Engineering, Nagpur",
  "Shri Ramdeobaba College of Engineering, Nagpur",
  "G.H. Raisoni College of Engineering, Nagpur",
  "Priyadarshini College of Engineering, Nagpur",
  "Laxminarayan Institute of Technology (LIT), Nagpur",
  // ── Nashik Region ──
  "K.K. Wagh College of Engineering, Nashik",
  "NDMVPS's K.B.T. College of Engineering, Nashik",
  "Sandip University, Nashik",
  "MGM College of Engineering, Nashik",
  // ── Aurangabad / Marathwada ──
  "MGM College of Engineering, Aurangabad",
  "Government College of Engineering, Aurangabad",
  "Deogiri Institute of Engineering, Aurangabad",
  "Shri Guru Gobind Singhji Institute of Engineering (SGGS), Nanded",
  // ── Kolhapur / Sangli ──
  "Shivaji University, Kolhapur",
  "KIT College of Engineering, Kolhapur",
  "Walchand College of Engineering, Sangli",
  "Government College of Engineering, Karad",
  // ── Amravati / Vidarbha ──
  "Government College of Engineering, Amravati",
  "PRMIT & R, Badnera, Amravati",
  "Sipna College of Engineering, Amravati",
  // ── Other National ──
  "Manipal Institute of Technology",
  "SRM Institute of Science and Technology",
  "Amity University",
  "Other",
];

// Disposable/temp email domains blocked to prevent fraud
const BLOCKED_EMAIL_DOMAINS = new Set([
  "mailinator.com","guerrillamail.com","tempmail.com","throwaway.email",
  "yopmail.com","sharklasers.com","guerrillamailblock.com","grr.la",
  "guerrillamail.info","spam4.me","trashmail.com","trashmail.me",
  "trashmail.net","dispostable.com","mailnull.com","spamgourmet.com",
  "spamgourmet.net","getairmail.com","filzmail.com","spamhereplease.com",
  "spamhere.eu","nomail.xl.cx","spamfree24.org","spamfree24.de",
  "spamfree24.eu","spamfree24.net","spamfree24.info","tempinbox.com",
  "tempr.email","discard.email","spamgourmet.org","maildrop.cc",
  "fakeinbox.com","mailnesia.com","mailnull.com","spamgourmet.com",
  "0-mail.com","0815.ru","0clickemail.com","0wnd.net","0wnd.org",
  "10minutemail.com","10minutemail.net","20minutemail.com","33mail.com",
  "mailtemp.info","mytemp.email","tmpmail.net","tmpmail.org",
]);

function isDisposableEmail(email) {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain && BLOCKED_EMAIL_DOMAINS.has(domain);
}

const COLLEGE_KEYWORDS = ["college","university","institute","school","academy","vidyalaya","polytechnic","iit","nit","bits","deemed","faculty","mahavidyalaya","engineering","technology","science","arts","commerce","medical","pharmacy","management","law","education","nursing","dental","ayurved"];

function validateCollegeName(name) {
  if (!name || name.trim().length < 8) return "College name must be at least 8 characters.";
  const lower = name.trim().toLowerCase();
  const hasKeyword = COLLEGE_KEYWORDS.some(k => lower.includes(k));
  if (!hasKeyword) return "Enter a valid college name (must include words like 'College', 'University', 'Institute', etc.)";
  return null;
}

const WEAK_PASSWORDS = new Set([
  "12345678","123456789","1234567890","password","password1","password123",
  "abcdefgh","qwerty123","qwertyui","iloveyou","welcome1","sunshine1",
  "11111111","00000000","99999999","12341234","11223344","abc12345",
  "pass1234","letmein1","monkey12","dragon12","master12","football",
  "baseball","superman","batman123","india123","india@123","college1",
]);

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "" };
  if (pw.length < 8) return { score: 1, label: "Too short", color: "bg-red-500" };
  if (WEAK_PASSWORDS.has(pw.toLowerCase())) return { score: 1, label: "Too common", color: "bg-red-500" };
  if (/^(.)\1+$/.test(pw)) return { score: 1, label: "Too simple", color: "bg-red-500" };
  const has_upper = /[A-Z]/.test(pw);
  const has_lower = /[a-z]/.test(pw);
  const has_digit = /\d/.test(pw);
  const has_special = /[^A-Za-z0-9]/.test(pw);
  const score = [has_upper, has_lower, has_digit, has_special].filter(Boolean).length;
  if (score <= 1) return { score: 2, label: "Weak", color: "bg-orange-400" };
  if (score === 2) return { score: 3, label: "Fair", color: "bg-yellow-400" };
  if (score === 3) return { score: 4, label: "Good", color: "bg-blue-500" };
  return { score: 5, label: "Strong", color: "bg-green-500" };
}

const CHILD_CLASSES = [
  "Nursery", "LKG", "UKG",
  "Class 1", "Class 2", "Class 3", "Class 4", "Class 5",
  "Class 6", "Class 7", "Class 8",
  "Class 9", "Class 10", "Class 11", "Class 12",
  "Graduation / College",
];

export default function Signup() {
  const { signupDirect, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [signupEmail, setSignupEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [userType, setUserType] = useState("student");
  const [studentForm, setStudentForm] = useState({ name: "", email: "", phone: "", college: "", password: "", confirm: "" });
  const [otherCollege, setOtherCollege] = useState("");
  const [parentForm, setParentForm] = useState({ name: "", email: "", phone: "", childSchool: "", childClass: "", city: "", password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function handleStudentChange(e) { setStudentForm({ ...studentForm, [e.target.name]: e.target.value }); }
  function handleParentChange(e) { setParentForm({ ...parentForm, [e.target.name]: e.target.value }); }

  async function handleGoogleSignup() {
    try {
      setError(""); setLoading(true);
      await loginWithGoogle();
      navigate("/");
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") return;
      if (err.code === "account-banned") setError("Your account has been removed. Contact support@campusswap.in.");
      else setError("Google sign-in failed. Please try again.");
    } finally { setLoading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const f = userType === "student" ? studentForm : parentForm;
    if (!f.name.trim()) return setError("Please enter your name.");
    if (!f.email.trim()) return setError("Please enter your email.");
    if (isDisposableEmail(f.email.trim())) return setError("Temporary/disposable email addresses are not allowed. Please use your real email.");
    if (!/^[6-9]\d{9}$/.test(f.phone.replace(/\D/g, ""))) return setError("Enter a valid 10-digit Indian mobile number.");
    if (userType === "student" && !f.college) return setError("Please select your college.");
    if (userType === "student" && f.college === "Other") {
      if (!otherCollege.trim()) return setError("Please enter your college name.");
      const collegeErr = validateCollegeName(otherCollege);
      if (collegeErr) return setError(collegeErr);
    }
    if (userType === "parent" && !f.childSchool.trim()) return setError("Please enter your child's school name.");
    if (userType === "parent" && !f.childClass) return setError("Please select your child's class.");
    if (userType === "parent" && !f.city.trim()) return setError("Please enter your city.");
    if (f.password.length < 8) return setError("Password must be at least 8 characters.");
    const pwStrength = getPasswordStrength(f.password);
    if (pwStrength.score <= 2) return setError("Password is too weak. Mix uppercase, lowercase, numbers and symbols.");
    if (f.password !== f.confirm) return setError("Passwords do not match.");

    const profileData = userType === "student" ? {
      userType: "student", name: f.name.trim(), email: f.email.trim(),
      phone: f.phone.replace(/\D/g, ""), college: f.college === "Other" ? otherCollege.trim() : f.college, password: f.password,
    } : {
      userType: "parent", name: f.name.trim(), email: f.email.trim(),
      phone: f.phone.replace(/\D/g, ""), childSchool: f.childSchool.trim(),
      childClass: f.childClass, city: f.city.trim(), password: f.password,
    };

    try {
      setLoading(true);
      await signupDirect(profileData);
      setSignupEmail(profileData.email);
      setEmailSent(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("An account with this email already exists. Please login.");
      else if (err.code === "auth/invalid-email") setError("Please enter a valid email address.");
      else if (err.code === "auth/network-request-failed") setError("Network error. Check your internet connection.");
      else setError("Failed to create account. Please try again.");
    } finally { setLoading(false); }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex flex-col justify-center px-6 py-10">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-white">Campus<span className="text-yellow-300">Swap</span></h1>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-auto w-full text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Mail size={28} className="text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-800">Check your email!</h2>
          <p className="text-sm text-gray-500">
            We sent a verification link to<br />
            <strong className="text-gray-800">{signupEmail}</strong>
          </p>
          <p className="text-xs text-gray-400">Click the link in the email to activate your account. Then come back and login.</p>
          <p className="text-xs text-orange-500 font-medium">Not in inbox? Check your Spam / Junk folder.</p>
          <button onClick={() => navigate("/login")}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex flex-col justify-center px-6 py-10">
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-white">Campus<span className="text-yellow-300">Swap</span></h1>
        <p className="text-blue-200 mt-1 text-sm">Join your campus marketplace</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-auto w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create Account</h2>

        {/* User type toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5">
          <button type="button" onClick={() => { setUserType("student"); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${userType === "student" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500"}`}>
            <GraduationCap size={16} /> Student
          </button>
          <button type="button" onClick={() => { setUserType("parent"); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${userType === "parent" ? "bg-amber-500 text-white shadow-sm" : "text-gray-500"}`}>
            <Users size={16} /> Parent
          </button>
        </div>

        {/* Google Sign-Up */}
        <button
          type="button"
          onClick={handleGoogleSignup}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50 transition shadow-sm disabled:opacity-60 mb-4"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 font-medium">or sign up with email</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />{error}
          </div>
        )}

        {userType === "student" && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative"><User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="name" value={studentForm.name} onChange={handleStudentChange} placeholder="Full Name" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
            <div className="relative"><Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="email" type="email" value={studentForm.email} onChange={handleStudentChange} placeholder="Email Address" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
            <div className="relative"><Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="phone" type="tel" value={studentForm.phone} onChange={handleStudentChange} placeholder="10-digit mobile number" required maxLength={10} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
            <CollegeSearch
              value={studentForm.college}
              onChange={(val) => setStudentForm({ ...studentForm, college: val })}
              required
            />
            <div>
              <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="password" type={showPassword ? "text" : "password"} value={studentForm.password} onChange={handleStudentChange} placeholder="Password (min 8 chars)" required className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
              {studentForm.password.length > 0 && (() => { const s = getPasswordStrength(studentForm.password); return (
                <div className="mt-1.5 px-1">
                  <div className="flex gap-1 mb-1">{[1,2,3,4].map(i => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${s.score > i ? s.color : "bg-gray-200"}`} />)}</div>
                  <p className={`text-xs font-medium ${s.score <= 2 ? "text-red-500" : s.score === 3 ? "text-yellow-600" : s.score === 4 ? "text-blue-600" : "text-green-600"}`}>{s.label}</p>
                </div>
              ); })()}
            </div>
            <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="confirm" type={showConfirm ? "text" : "password"} value={studentForm.confirm} onChange={handleStudentChange} placeholder="Confirm Password" required className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /><button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">{loading ? "Creating account..." : "Create Account →"}</button>
          </form>
        )}

        {userType === "parent" && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative"><User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="name" value={parentForm.name} onChange={handleParentChange} placeholder="Parent's Full Name" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /></div>
            <div className="relative"><Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="email" type="email" value={parentForm.email} onChange={handleParentChange} placeholder="Email Address" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /></div>
            <div className="relative"><Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="phone" type="tel" value={parentForm.phone} onChange={handleParentChange} placeholder="10-digit mobile number" required maxLength={10} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /></div>
            <div className="relative"><BookOpen size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="childSchool" value={parentForm.childSchool} onChange={handleParentChange} placeholder="Child's School Name" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /></div>
            <div className="relative"><GraduationCap size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><select name="childClass" value={parentForm.childClass} onChange={handleParentChange} required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-700 appearance-none bg-white"><option value="">Child's Current Class</option>{CHILD_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div className="relative"><MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="city" value={parentForm.city} onChange={handleParentChange} placeholder="Your City" required className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /></div>
            <div>
              <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="password" type={showPassword ? "text" : "password"} value={parentForm.password} onChange={handleParentChange} placeholder="Password (min 8 chars)" required className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
              {parentForm.password.length > 0 && (() => { const s = getPasswordStrength(parentForm.password); return (
                <div className="mt-1.5 px-1">
                  <div className="flex gap-1 mb-1">{[1,2,3,4].map(i => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${s.score > i ? s.color : "bg-gray-200"}`} />)}</div>
                  <p className={`text-xs font-medium ${s.score <= 2 ? "text-red-500" : s.score === 3 ? "text-yellow-600" : s.score === 4 ? "text-blue-600" : "text-green-600"}`}>{s.label}</p>
                </div>
              ); })()}
            </div>
            <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input name="confirm" type={showConfirm ? "text" : "password"} value={parentForm.confirm} onChange={handleParentChange} placeholder="Confirm Password" required className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" /><button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
            <button type="submit" disabled={loading} className="w-full bg-amber-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-amber-600 disabled:opacity-60">{loading ? "Creating account..." : "Create Account →"}</button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 font-semibold hover:underline">Sign In</Link>
        </p>
        <p className="text-center text-xs text-gray-400 mt-3">
          By signing up you agree to our{" "}
          <Link to="/terms" className="text-blue-500 hover:underline">Terms</Link>{" "}
          &amp;{" "}
          <Link to="/privacy-policy" className="text-blue-500 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
